import { ENV_VARIABLES, DEFAULT_VALUES } from './constants';

// Define the configuration structure
export interface Config {
  apiUrl: string;
  socketUrl: string;
  stripeKey: string;
  redisUrl: string;
  cdnUrl: string;
  apiServerUrl?: string;
  auth0: {
    domain: string;
    clientId: string;
    audience: string;
    redirectUri?: string;
    claimsNamespace: string;
  };
}

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentConfig {
  development: Config;
  staging: Config;
  production: Config;
}

/**
 * Gets an environment variable value with fallback
 */
const getEnvVar = (key: string, fallback = ''): string => {
  return import.meta.env[key] || fallback;
};

/**
 * Creates config for a specific environment
 * Note: Validation happens at runtime in validation.ts, not during config creation
 */
const createEnvironmentConfig = (env: Environment): Config => {
  const defaults = DEFAULT_VALUES[env];
  const stripeKeyVar =
    env === 'production'
      ? ENV_VARIABLES.STRIPE_KEY.PRODUCTION
      : ENV_VARIABLES.STRIPE_KEY.TEST;

  const stripeKeyValue = getEnvVar(stripeKeyVar, defaults.stripeKey);

  // Debug logging for Stripe key (only in non-production and non-test)
  if (env !== 'production' && import.meta.env.MODE !== 'test') {
    console.log(`[Config Debug] Environment: ${env}`);
    console.log(`[Config Debug] Looking for Stripe key: ${stripeKeyVar}`);
    console.log(
      `[Config Debug] Stripe key found: ${stripeKeyValue ? 'YES (length: ' + stripeKeyValue.length + ')' : 'NO'}`,
    );
  }

  const config = {
    apiUrl: getEnvVar(ENV_VARIABLES.API_URL, defaults.apiUrl),
    apiServerUrl: getEnvVar(
      ENV_VARIABLES.API_SERVER_URL,
      defaults.apiServerUrl,
    ),
    socketUrl: getEnvVar(ENV_VARIABLES.SOCKET_URL, defaults.socketUrl),
    stripeKey: stripeKeyValue,
    redisUrl: getEnvVar(ENV_VARIABLES.REDIS_URL, defaults.redisUrl),
    cdnUrl: getEnvVar(ENV_VARIABLES.CDN_URL, defaults.cdnUrl),
    auth0: {
      domain: getEnvVar(ENV_VARIABLES.AUTH0_DOMAIN, defaults.auth0.domain),
      clientId: getEnvVar(
        ENV_VARIABLES.AUTH0_CLIENT_ID,
        defaults.auth0.clientId,
      ),
      audience: getEnvVar(
        ENV_VARIABLES.AUTH0_AUDIENCE,
        defaults.auth0.audience,
      ),
      claimsNamespace: getEnvVar(
        'VITE_AUTH0_CLAIMS_NAMESPACE',
        'https://donvio.com/',
      ),
    },
  };

  // NOTE: Validation is now only done at runtime in validation.ts
  // Not during build-time config creation to avoid false errors for non-active environments

  return config;
};

/**
 * Determines the current environment
 */
export const getEnvironment = (): Environment => {
  const env = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

  switch (env) {
    case 'staging':
      return 'staging';
    case 'production':
      return 'production';
    default:
      return 'development';
  }
};

// Environment-specific configuration
const config: EnvironmentConfig = {
  development: createEnvironmentConfig('development'),
  staging: createEnvironmentConfig('staging'),
  production: createEnvironmentConfig('production'),
};

// Only log config in development for debugging (not during tests)
if (import.meta.env.MODE === 'development' && !import.meta.env.VITEST) {
  console.debug('CONFIG:', config);
}

export default config;
